import type { SceneDocument, SceneElement } from "./schema";

/** Geometry helpers shared by the editor, the preflight and the renderer. */

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A rectangle rotated around its centre (degrees, clockwise). */
export interface Box extends Rect {
  rotation: number;
}

const RAD = Math.PI / 180;

export function rotatePoint(point: Point, center: Point, degrees: number): Point {
  if (!degrees) return { ...point };
  const angle = degrees * RAD;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos };
}

export function boxCenter(box: Rect): Point {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

/** Corners in world space: top-left, top-right, bottom-right, bottom-left. */
export function boxCorners(box: Box): [Point, Point, Point, Point] {
  const center = boxCenter(box);
  const { x, y, width, height, rotation } = box;
  return [
    rotatePoint({ x, y }, center, rotation),
    rotatePoint({ x: x + width, y }, center, rotation),
    rotatePoint({ x: x + width, y: y + height }, center, rotation),
    rotatePoint({ x, y: y + height }, center, rotation),
  ];
}

/** Axis-aligned bounding box of a (possibly rotated) box. */
export function boundingRect(box: Box): Rect {
  if (!box.rotation || box.rotation % 360 === 0) {
    return { x: box.x, y: box.y, width: box.width, height: box.height };
  }
  return rectFromPoints(boxCorners(box));
}

export function rectFromPoints(points: Point[]): Rect {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function unionRects(rects: Rect[]): Rect | null {
  if (rects.length === 0) return null;
  return rectFromPoints(
    rects.flatMap((rect) => [
      { x: rect.x, y: rect.y },
      { x: rect.x + rect.width, y: rect.y + rect.height },
    ]),
  );
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

export function rectContains(outer: Rect, inner: Rect, tolerance = 0): boolean {
  return (
    inner.x >= outer.x - tolerance &&
    inner.y >= outer.y - tolerance &&
    inner.x + inner.width <= outer.x + outer.width + tolerance &&
    inner.y + inner.height <= outer.y + outer.height + tolerance
  );
}

export function normalizeDegrees(degrees: number): number {
  const value = degrees % 360;
  return value < 0 ? value + 360 : value;
}

// ---------------------------------------------------------------------------
// Scene structure
// ---------------------------------------------------------------------------

/** Where an element lives: on a page (top level) or inside a group. */
export type ElementParent =
  | { kind: "page"; pageIndex: number }
  | { kind: "group"; groupId: string };

export function buildParentMap(doc: SceneDocument): Map<string, ElementParent> {
  const parents = new Map<string, ElementParent>();
  doc.pages.forEach((page, pageIndex) => {
    for (const childId of page.children) parents.set(childId, { kind: "page", pageIndex });
  });
  for (const element of Object.values(doc.elements)) {
    if (element.type === "group") {
      for (const childId of element.children)
        parents.set(childId, { kind: "group", groupId: element.id });
    }
  }
  return parents;
}

/** Page index an element (or one of its ancestors) is placed on, or -1. */
export function pageIndexOf(
  doc: SceneDocument,
  elementId: string,
  parents = buildParentMap(doc),
): number {
  let current = parents.get(elementId);
  let guard = 0;
  while (current && guard++ < 100) {
    if (current.kind === "page") return current.pageIndex;
    current = parents.get(current.groupId);
  }
  return -1;
}

/** Top-level ancestor on the page (the element itself when it is not grouped). */
export function rootOf(elementId: string, parents: Map<string, ElementParent>): string {
  let id = elementId;
  let parent = parents.get(id);
  let guard = 0;
  while (parent?.kind === "group" && guard++ < 100) {
    id = parent.groupId;
    parent = parents.get(id);
  }
  return id;
}

/** Converts a box in a group's local space into the group's parent space. */
export function toParentSpace(child: Box, group: Box): Box {
  const groupCenter = boxCenter(group);
  const local = {
    x: group.x + child.x + child.width / 2,
    y: group.y + child.y + child.height / 2,
  };
  const center = rotatePoint(local, groupCenter, group.rotation);
  return {
    x: center.x - child.width / 2,
    y: center.y - child.height / 2,
    width: child.width,
    height: child.height,
    rotation: child.rotation + group.rotation,
  };
}

/** Inverse of `toParentSpace`. */
export function toGroupSpace(box: Box, group: Box): Box {
  const groupCenter = boxCenter(group);
  const center = rotatePoint(boxCenter(box), groupCenter, -group.rotation);
  return {
    x: center.x - group.x - box.width / 2,
    y: center.y - group.y - box.height / 2,
    width: box.width,
    height: box.height,
    rotation: box.rotation - group.rotation,
  };
}

export function elementBox(element: SceneElement): Box {
  return {
    x: element.x,
    y: element.y,
    width: element.width,
    height: element.height,
    rotation: element.rotation ?? 0,
  };
}

/** Box of an element in page coordinates, taking all enclosing groups into account. */
export function worldBox(
  doc: SceneDocument,
  elementId: string,
  parents = buildParentMap(doc),
): Box | null {
  const element = doc.elements[elementId];
  if (!element) return null;
  let box = elementBox(element);
  let parent = parents.get(elementId);
  let guard = 0;
  while (parent?.kind === "group" && guard++ < 100) {
    const group = doc.elements[parent.groupId];
    if (!group) break;
    box = toParentSpace(box, elementBox(group));
    parent = parents.get(group.id);
  }
  return box;
}

export interface FlatElement {
  element: SceneElement;
  /** Box in page coordinates. */
  box: Box;
  /** Opacity multiplied with all enclosing groups. */
  opacity: number;
  /** Hidden itself or through a hidden group. */
  hidden: boolean;
  depth: number;
}

/**
 * All leaf elements of a page in paint order (groups are expanded),
 * with page-space boxes.
 */
export function flattenPage(doc: SceneDocument, pageIndex: number): FlatElement[] {
  const page = doc.pages[pageIndex];
  if (!page) return [];
  const out: FlatElement[] = [];
  const visit = (
    ids: string[],
    parentBox: Box | null,
    opacity: number,
    hidden: boolean,
    depth: number,
  ) => {
    for (const childId of ids) {
      const element = doc.elements[childId];
      if (!element || depth > 50) continue;
      const own = elementBox(element);
      const box = parentBox ? toParentSpace(own, parentBox) : own;
      const nextOpacity = opacity * (element.opacity ?? 1);
      const nextHidden = hidden || element.hidden;
      if (element.type === "group") {
        visit(element.children, box, nextOpacity, nextHidden, depth + 1);
      } else {
        out.push({ element, box, opacity: nextOpacity, hidden: nextHidden, depth });
      }
    }
  };
  visit(page.children, null, 1, false, 0);
  return out;
}

/** Ids of an element and all of its descendants. */
export function descendantIds(doc: SceneDocument, elementId: string): string[] {
  const out: string[] = [];
  const visit = (id: string, depth: number) => {
    const element = doc.elements[id];
    if (!element || depth > 50) return;
    out.push(id);
    if (element.type === "group") for (const child of element.children) visit(child, depth + 1);
  };
  visit(elementId, 0);
  return out;
}
